import type { Entity } from '@backstage/catalog-model';
import type { CatalogApi } from '@backstage/catalog-client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityPresentationApiRef,
  type EntityPresentationApi,
} from '@backstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import {
  OwnedEntityModel,
  sortOwnedEntityOptions,
  type OwnedEntityOption,
} from './OwnedEntityModel';
import { QUERY_KEYS } from '../../queryKeys';
import { buildOwnedEntityFilters, getOwnershipEntityRefs } from './ownership';

export function useOwnedEntities(techDocsOnly: boolean) {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);

  return useQuery({
    queryKey: QUERY_KEYS.scaffolder.ownedEntities(techDocsOnly),
    queryFn: async (): Promise<OwnedEntityOption[]> => {
      const identity = await identityApi.getBackstageIdentity();
      const ownershipEntityRefs = getOwnershipEntityRefs(identity);
      const entities = await retrieveOwnedEntities(catalogApi, ownershipEntityRefs);
      const ownedEntities = entities.map(entity =>
        toOwnedEntityModel(entity, entityPresentationApi),
      );

      return ownedEntities
        .filter(entity => entity.shouldInclude(techDocsOnly))
        .map(entity => entity.toOption())
        .sort(sortOwnedEntityOptions);
    },
  });
}

async function retrieveOwnedEntities(
  catalogApi: Pick<CatalogApi, 'getEntities'>,
  ownershipEntityRefs: string[],
): Promise<Entity[]> {
  const response = await catalogApi.getEntities({
    filter: buildOwnedEntityFilters(ownershipEntityRefs),
    fields: ['kind', 'metadata'],
  });

  return response.items;
}

function toOwnedEntityModel(
  entity: Entity,
  entityPresentationApi: Pick<EntityPresentationApi, 'forEntity'>,
): OwnedEntityModel {
  const presentationSnapshot = entityPresentationApi.forEntity(entity).snapshot;

  return OwnedEntityModel.fromEntity(entity, presentationSnapshot);
}
